import { useEffect, useState } from 'react';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import IconaOcchio from '../../components/IconaOcchio';
import { etichettaImpresa, ordinaPerPratica } from '../../utils/pratica';

const vuotoForm = { email: '', password: '', ruolo: 'impresa', impresa_id: '', attivo: true };

export default function GestioneUtenti() {
  const [utenti, setUtenti] = useState([]);
  const [imprese, setImprese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(vuotoForm);
  const [editId, setEditId] = useState(null);
  const [mostraForm, setMostraForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState('');
  const [mostraPassword, setMostraPassword] = useState(false);
  // Password che l'utente ha attualmente (decifrata dal server): serve a capire se l'admin l'ha cambiata
  const [passwordOriginale, setPasswordOriginale] = useState('');
  const [copiata, setCopiata] = useState(false);

  function carica() {
    Promise.all([
      axios.get('/api/utenti'),
      axios.get('/api/imprese'),
    ]).then(([u, i]) => {
      setUtenti(u.data);
      setImprese(i.data);
    }).finally(() => setLoading(false));
  }

  useEffect(() => { carica(); }, []);

  function apriNuovo() {
    setForm(vuotoForm);
    setPasswordOriginale('');
    setEditId(null);
    setErrore('');
    setMostraForm(true);
    setTimeout(() => document.getElementById('form-utente')?.scrollIntoView({ behavior: 'smooth' }), 50);
  }

  function apriModifica(u) {
    setForm({ email: u.email, password: '', ruolo: u.ruolo, impresa_id: u.impresa_id || '', attivo: u.attivo });
    setPasswordOriginale('');
    setEditId(u.id);
    setErrore('');
    setMostraForm(true);
    setTimeout(() => document.getElementById('form-utente')?.scrollIntoView({ behavior: 'smooth' }), 50);
    // Recupera la password attuale (se disponibile) così l'admin può vederla e rinviarla
    axios.get(`/api/utenti/${u.id}/password`).then(r => {
      const p = r.data.password || '';
      setPasswordOriginale(p);
      setForm(f => (f.password === '' ? { ...f, password: p } : f));
    }).catch(() => {});
  }

  async function copiaPassword() {
    try {
      await navigator.clipboard.writeText(form.password);
      setCopiata(true);
      setTimeout(() => setCopiata(false), 1500);
    } catch { /* copia non disponibile: la password resta visibile col pulsante occhio */ }
  }

  async function salva(e) {
    e.preventDefault();
    setSaving(true);
    setErrore('');
    try {
      const payload = { ...form, impresa_id: form.impresa_id || null };
      // In modifica la password si invia solo se è stata cambiata
      if (editId && (!payload.password || payload.password === passwordOriginale)) delete payload.password;
      if (editId) {
        await axios.put(`/api/utenti/${editId}`, payload);
      } else {
        await axios.post('/api/utenti', payload);
      }
      setMostraForm(false);
      carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  async function elimina(id, email) {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    try {
      await axios.delete(`/api/utenti/${id}`);
      carica();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  }

  async function toggleAttivo(u) {
    try {
      await axios.put(`/api/utenti/${u.id}`, { attivo: !u.attivo });
      carica();
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.error || err.message));
    }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-[#1A3A5C]">Gestione utenti</h1>
        <button
          onClick={apriNuovo}
          className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition"
        >
          + Nuovo utente
        </button>
      </div>

      {mostraForm && (
        <form id="form-utente" onSubmit={salva} className="bg-white rounded-xl shadow p-5 mb-6">
          <h3 className="font-semibold text-[#1A3A5C] mb-4">{editId ? 'Modifica utente' : 'Nuovo utente'}</h3>
          {errore && <p className="text-red-600 text-sm mb-3">{errore}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
              <input
                required type="email" value={form.email}
                onChange={e => set('email', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">Password</label>
                {form.password && (
                  <button type="button" onClick={copiaPassword} className="text-xs text-[#2E6096] hover:underline">
                    {copiata ? 'Copiata' : 'Copia'}
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={mostraPassword ? 'text' : 'password'} value={form.password}
                  onChange={e => set('password', e.target.value)}
                  required={!editId}
                  placeholder={editId && !passwordOriginale ? 'Non disponibile: scrivine una nuova per impostarla' : ''}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                />
                <button type="button" onClick={() => setMostraPassword(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <IconaOcchio barrato={mostraPassword} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Ruolo *</label>
              <select
                value={form.ruolo} onChange={e => set('ruolo', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              >
                <option value="impresa">Impresa (cliente)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Impresa collegata {form.ruolo === 'admin' && <span className="text-gray-400">(non necessaria per admin)</span>}
              </label>
              <select
                value={form.impresa_id} onChange={e => set('impresa_id', e.target.value)}
                disabled={form.ruolo === 'admin'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096] disabled:bg-gray-100"
              >
                <option value="">— nessuna —</option>
                {ordinaPerPratica(imprese).map(i => (
                  <option key={i.id} value={i.id}>{etichettaImpresa(i)}</option>
                ))}
              </select>
            </div>
          </div>
          {editId && (
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-4 cursor-pointer">
              <input type="checkbox" checked={form.attivo} onChange={e => set('attivo', e.target.checked)} className="rounded" />
              Utente attivo
            </label>
          )}
          <div className="flex gap-3">
            <button type="submit" disabled={saving}
              className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition disabled:opacity-50">
              {saving ? 'Salvo...' : 'Salva'}
            </button>
            <button type="button" onClick={() => setMostraForm(false)} className="text-gray-500 text-sm hover:text-gray-700">
              Annulla
            </button>
          </div>
        </form>
      )}

      {loading ? <Spinner /> : utenti.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Nessun utente presente</div>
      ) : (
        <>
          {/* Card mobile */}
          <div className="flex flex-col gap-3 sm:hidden">
            {utenti.map(u => (
              <div key={u.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-[#1A3A5C] text-sm break-all">{u.email}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{u.impresa ? etichettaImpresa(u.impresa) : '—'}</p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${u.ruolo === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {u.ruolo === 'admin' ? 'Admin' : 'Impresa'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <button onClick={() => toggleAttivo(u)}
                    className={`px-2 py-0.5 rounded-full text-xs font-bold cursor-pointer transition ${u.attivo ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}>
                    {u.attivo ? 'Attivo' : 'Disattivato'}
                  </button>
                  <div className="flex gap-4">
                    <button onClick={() => apriModifica(u)} className="text-[#2E6096] text-sm font-medium hover:underline">Modifica</button>
                    <button onClick={() => elimina(u.id, u.email)} className="text-red-500 text-sm hover:underline">Elimina</button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabella desktop */}
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Ruolo</th>
                  <th className="text-left px-4 py-3">Impresa collegata</th>
                  <th className="text-center px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {utenti.map((u, i) => (
                  <tr key={u.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-[#1A3A5C]">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.ruolo === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {u.ruolo === 'admin' ? 'Admin' : 'Impresa'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.impresa ? etichettaImpresa(u.impresa) : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggleAttivo(u)}
                        className={`px-2 py-0.5 rounded-full text-xs font-bold cursor-pointer transition ${u.attivo ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}>
                        {u.attivo ? 'Attivo' : 'Disattivato'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-3 justify-end">
                        <button onClick={() => apriModifica(u)} className="text-[#2E6096] hover:underline text-sm font-medium">Modifica</button>
                        <button onClick={() => elimina(u.id, u.email)} className="text-red-500 hover:underline text-sm">Elimina</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
